import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useResetPasswordMutation } from '../../../queries/auth';
import AuthShell from '../../../components/auth/AuthShell';
import { ErrorBanner, Field, PasswordField, Head, Submit, focusFirstInvalid } from '../../../components/auth/parts';
import { classify, firstError, isExpiredMessage } from '../../../components/auth/authErrors';
import { emailOk, MIN_PASSWORD } from '../../../components/auth/validators';
import useCountdown from '../../../components/auth/useCountdown';
import useAuthTitle from '../../../components/auth/useAuthTitle';

function ResetPassword () {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const a = (k) => t(`authPage.${k}`);
  const S = (k) => t(`authPage.reset.${k}`);

  const { token } = useParams();
  // Older reset links (sent before the `?email=` fix) have no email — those still
  // need the editable fallback. Newer links carry it, shown read-only: the
  // backend's token+email match is the real security check; this just saves the
  // user retyping it.
  const [searchParams] = useSearchParams();
  const emailFromLink = searchParams.get('email');

  const [email, setEmail] = useState(emailFromLink || '');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [errs, setErrs] = useState({});
  const [view, setView] = useState('form'); // form | expired | invalid
  const [banner, setBanner] = useState(null); // rate | offline | server
  const formRef = useRef(null);
  const cd = useCountdown();
  const reset = useResetPasswordMutation({ inline: true });
  const locked = banner === 'rate' && cd.left > 0;

  useAuthTitle('reset');
  useEffect(() => { if (banner === 'rate' && cd.left === 0) setBanner(null); }, [banner, cd.left]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (locked || reset.isLoading) return;
    const n = {};
    if (!emailFromLink) { if (!email.trim()) n.email = a('eReq'); else if (!emailOk(email)) n.email = a('eEmail'); }
    if (!password) n.password = a('eReq'); else if (password.length < MIN_PASSWORD) n.password = a('eShort');
    if (!password2) n.password2 = a('eReq'); else if (password2 !== password) n.password2 = a('eMatch');
    setErrs(n);
    if (Object.keys(n).length) { focusFirstInvalid(formRef.current); return; }

    setBanner(null);
    try {
      // Same body as before: email (from the link, else typed) + the two passwords.
      await reset.mutateAsync({
        token,
        data: { password, password_confirmation: password2, email: emailFromLink ?? email.trim() },
      });
    } catch (err) {
      const info = classify(err);
      if (info.kind === 'unauthorized') setView(isExpiredMessage(info.message) ? 'expired' : 'invalid');
      else if (info.kind === 'rate') { cd.start(info.retryAfter); setBanner('rate'); }
      else if (info.kind === 'offline') setBanner('offline');
      else if (info.kind === 'validation' && (firstError(info.errors, 'password', 'email'))) {
        const pw = firstError(info.errors, 'password');
        setErrs({
          password: pw && !/confirm/i.test(pw) ? a('eShort') : '',
          password2: pw && /confirm/i.test(pw) ? a('eMatch') : '',
          email: firstError(info.errors, 'email') ? a('eEmail') : '',
        });
      } else setBanner('server');
    }
  };

  if (view !== 'form') {
    const exp = view === 'expired';
    return (
      <AuthShell>
        <Head icon={exp ? 'clock' : 'broken'} tone={exp ? '' : 'crit'} title={exp ? S('expH') : S('invH')} text={exp ? S('expP') : S('invP')} />
        <button className="ppa-btn" type="button" onClick={() => navigate('/forget/password')}>{S('again')}</button>
        <p className="ppa-alt"><Link to="/login">{t('authPage.forgot.back')}</Link></p>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Head icon="key" title={S('h')} text={S('p')} />
      <form ref={formRef} noValidate onSubmit={onSubmit}>
        {banner && <ErrorBanner kind={banner} left={cd.left} />}
        <Field
          id="email" label={a('email')} type="email" ltr autoComplete="email" readOnly={!!emailFromLink}
          value={email} error={errs.email}
          onChange={(e) => { setEmail(e.target.value); setErrs((x) => ({ ...x, email: '' })); }}
        />
        <PasswordField id="password" label={a('newpw')} autoComplete="new-password" value={password}
          onChange={(e) => { setPassword(e.target.value); setErrs((x) => ({ ...x, password: '' })); }} error={errs.password} checklist />
        <PasswordField id="password2" label={a('pw2')} autoComplete="new-password" value={password2}
          onChange={(e) => { setPassword2(e.target.value); setErrs((x) => ({ ...x, password2: '' })); }} error={errs.password2} />
        <Submit busy={reset.isLoading} busyLabel={S('busy')} disabled={locked}>{S('btn')}</Submit>
      </form>
    </AuthShell>
  );
}

export default ResetPassword;
